"use client";

import { useState, useEffect } from "react";
import { Search, Check, Building2, Loader2, ArrowRight, Shield } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Badge } from "@/app/_module/components/ui/badge";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

interface Carrier {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
}

interface CarrierSelectionProps {
  onComplete: (selectedCarriers: string[]) => void;
  isLoading: boolean;
  inviteContext?: {
    agencyId: string;
    agencyName: string;
    role: string;
    accountType?: 'agent' | 'sub_agency_owner'; // New: determine if creating sub-agency
  };
}

export function CarrierSelection({ onComplete, isLoading: externalLoading, inviteContext }: CarrierSelectionProps) {
  const { toast } = useToast();

  const [availableCarriers, setAvailableCarriers] = useState<Carrier[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<Set<string>>(new Set());
  const [lockedCarriers, setLockedCarriers] = useState<Set<string>>(new Set()); // Carriers inherited from parent
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(true);
  const [agencyCarriers, setAgencyCarriers] = useState<Carrier[]>([]); // Parent agency carriers

  useEffect(() => {
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    try {
      setIsLoadingCarriers(true);

      // If invited, fetch parent agency carriers first
      if (inviteContext?.agencyId) {
        const agencyResponse = await handleApiCall(async () => {
          return await api.get({
            url: getApiUrl(`/agency-carriers?agencyId=${inviteContext.agencyId}`)
          });
        });

        if (agencyResponse && Array.isArray(agencyResponse)) {
          const parentCarriers = agencyResponse.map((ac: any) => ({
            id: ac.carrierId,
            slug: ac.carrierSlug,
            name: ac.carrierName,
            isActive: ac.isEnabled,
          }));

          setAgencyCarriers(parentCarriers);

          // If regular agent (not sub-agency owner), lock parent carriers
          if (inviteContext.accountType === 'agent') {
            const lockedSlugs = new Set(parentCarriers.map((c: Carrier) => c.slug));
            setLockedCarriers(lockedSlugs);
            setSelectedCarriers(lockedSlugs);
          } else if (inviteContext.accountType === 'sub_agency_owner') {
            // Sub-agency owner: pre-select parent carriers but allow adding more
            const parentSlugs = parentCarriers.map((c: Carrier) => c.slug);
            setLockedCarriers(new Set(parentSlugs)); // Mark as inherited (can't remove)
            setSelectedCarriers(new Set(parentSlugs)); // Pre-selected
          }
        }
      }

      // Fetch all available carriers
      const response = await handleApiCall(async () => {
        return await api.get({
          url: getApiUrl('/agency-carriers/available')
        });
      });

      if (response) {
        setAvailableCarriers(Array.isArray(response) ? response : []);
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to load carriers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  const handleToggleCarrier = (slug: string) => {
    // Prevent removing locked carriers (inherited from parent)
    if (lockedCarriers.has(slug)) {
      toast({
        title: "Cannot Remove Carrier",
        description: inviteContext?.accountType === 'agent'
          ? `This carrier is assigned by ${inviteContext.agencyName} and cannot be removed.`
          : `This carrier is inherited from ${inviteContext?.agencyName || 'parent agency'} and must be included in your sub-agency.`,
        variant: "destructive",
      });
      return;
    }

    setSelectedCarriers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        newSet.add(slug);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredCarriers();
    setSelectedCarriers(new Set(filtered.map(c => c.slug)));
  };

  const handleDeselectAll = () => {
    // Keep locked carriers even when clearing
    setSelectedCarriers(new Set(lockedCarriers));
  };

  const handleContinue = () => {
    if (selectedCarriers.size === 0) {
      toast({
        title: "No Carriers Selected",
        description: "Please select at least one carrier to continue.",
        variant: "destructive",
      });
      return;
    }

    onComplete(Array.from(selectedCarriers));
  };

  const getFilteredCarriers = () => {
    if (!searchTerm.trim()) return availableCarriers;

    const searchLower = searchTerm.toLowerCase();
    return availableCarriers.filter(carrier =>
      carrier.name.toLowerCase().includes(searchLower) ||
      carrier.slug.toLowerCase().includes(searchLower)
    );
  };

  const filteredCarriers = getFilteredCarriers();
  const isLoading = isLoadingCarriers || externalLoading;

  if (isLoadingCarriers) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-muted-foreground">Loading carriers...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          Select Your Carriers
        </CardTitle>
        <CardDescription>
          {inviteContext ? (
            inviteContext.accountType === 'agent' ? (
              <>Carriers have been pre-selected by <strong>{inviteContext.agencyName}</strong>. These carriers cannot be changed.</>
            ) : inviteContext.accountType === 'sub_agency_owner' ? (
              <>
                As a sub-agency under <strong>{inviteContext.agencyName}</strong>, you'll inherit their carriers (shown below).
                You can select additional carriers for your team, but inherited carriers cannot be removed.
              </>
            ) : (
              <>Carriers from <strong>{inviteContext.agencyName}</strong> are pre-selected and required. You can add additional carriers for your sub-agency.</>
            )
          ) : (
            "Choose the insurance carriers you work with. You can always update this later in Settings."
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Invite Context Banner */}
        {inviteContext && agencyCarriers.length > 0 && (
          <Alert className={inviteContext.accountType === 'sub_agency_owner' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}>
            <Shield className={`h-4 w-4 ${inviteContext.accountType === 'sub_agency_owner' ? 'text-purple-600' : 'text-blue-600'}`} />
            <AlertDescription className={`text-sm ${inviteContext.accountType === 'sub_agency_owner' ? 'text-purple-900' : 'text-blue-900'}`}>
              {inviteContext.accountType === 'agent' ? (
                <>
                  <strong>{agencyCarriers.length} carrier{agencyCarriers.length !== 1 ? 's' : ''}</strong> assigned by {inviteContext.agencyName}.
                  These selections are locked and cannot be changed.
                </>
              ) : inviteContext.accountType === 'sub_agency_owner' ? (
                <>
                  <strong>Sub-Agency Carrier Inheritance:</strong> You're inheriting{' '}
                  <strong>{agencyCarriers.length} carrier{agencyCarriers.length !== 1 ? 's' : ''}</strong> from{' '}
                  {inviteContext.agencyName}. These are marked as "Inherited" below and are required for your sub-agency.
                  You can select additional carriers to expand your team's capabilities.
                </>
              ) : (
                <>
                  <strong>{agencyCarriers.length} carrier{agencyCarriers.length !== 1 ? 's' : ''}</strong> inherited from {inviteContext.agencyName}.
                  You can add more carriers for your sub-agency, but cannot remove inherited ones.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
        {/* Stats Bar */}
        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div>
            <p className="text-2xl font-bold text-blue-900">{selectedCarriers.size}</p>
            <p className="text-xs text-blue-600">Selected</p>
          </div>
          <div className="h-8 w-px bg-blue-300" />
          <div>
            <p className="text-2xl font-bold text-gray-700">{availableCarriers.length}</p>
            <p className="text-xs text-muted-foreground">Total Available</p>
          </div>
          {searchTerm && (
            <>
              <div className="h-8 w-px bg-blue-300" />
              <div>
                <p className="text-2xl font-bold text-gray-700">{filteredCarriers.length}</p>
                <p className="text-xs text-muted-foreground">Matching</p>
              </div>
            </>
          )}
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search carriers by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSelectAll}
              variant="outline"
              size="sm"
            >
              Select All {searchTerm && '(Filtered)'}
            </Button>
            <Button
              onClick={handleDeselectAll}
              variant="outline"
              size="sm"
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Carriers List */}
        <ScrollArea className="h-[400px] rounded-md border">
          <div className="p-4 space-y-2">
            {filteredCarriers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchTerm ? 'No carriers found matching your search.' : 'No carriers available.'}
                </p>
              </div>
            ) : (
              filteredCarriers.map((carrier) => {
                const isSelected = selectedCarriers.has(carrier.slug);
                const isLocked = lockedCarriers.has(carrier.slug);

                return (
                  <div
                    key={carrier.id}
                    onClick={() => handleToggleCarrier(carrier.slug)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      isLocked
                        ? inviteContext?.accountType === 'sub_agency_owner'
                          ? 'border-purple-400 bg-purple-100 cursor-not-allowed opacity-90'
                          : 'border-blue-400 bg-blue-100 cursor-not-allowed opacity-90'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50 cursor-pointer'
                        : 'border-gray-200 bg-white hover:border-blue-300 cursor-pointer'
                    }`}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isLocked}
                      onCheckedChange={() => handleToggleCarrier(carrier.slug)}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{carrier.name}</p>
                        {isLocked && (
                          <Badge variant="secondary" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            {inviteContext?.accountType === 'agent' ? 'Locked' : 'Inherited'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{carrier.slug}</p>
                    </div>

                    {isSelected && !isLocked && (
                      <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                    {isLocked && (
                      <Shield className={`h-5 w-5 flex-shrink-0 ${
                        inviteContext?.accountType === 'sub_agency_owner' ? 'text-purple-600' : 'text-blue-600'
                      }`} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Continue Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleContinue}
            disabled={selectedCarriers.size === 0 || isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue with {selectedCarriers.size} Carrier{selectedCarriers.size !== 1 ? 's' : ''}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
